import React, { useMemo, useState } from 'react';
import { useLMS } from '../../context/LMSContext';
import { exportCourseBackup } from '../../lib/exportBackup';

const AdminDashboard = () => {
  const {
    cohorts,
    students,
    setCurrentView,
    setCurrentCohort,
    addCohort,
    duplicateCohort,
    unreadMessageCount
  } = useLMS();

  const [showAddModal, setShowAddModal] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedCohortForDuplicate, setSelectedCohortForDuplicate] = useState(null);
  const [isDuplicating, setIsDuplicating] = useState(false);

  // Backup export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [includeStudentData, setIncludeStudentData] = useState(false);

  const [newCohort, setNewCohort] = useState({
    name: '',
    startDate: '',
    endDate: '',
    maxCapacity: 30
  });

  const [duplicateForm, setDuplicateForm] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });

  const safeCohorts = cohorts || [];
  const safeStudents = students || [];

  const totalStudents = useMemo(
    () => safeStudents.filter(u => u.role === 'student').length,
    [safeStudents]
  );

  const activeStudents = useMemo(
    () => safeStudents.filter(u => u.role === 'student' && u.status === 'active').length,
    [safeStudents]
  );

  const activeCohorts = useMemo(
    () => safeCohorts.filter(c => c.status === 'active').length,
    [safeCohorts]
  );

  const upcomingCohorts = useMemo(
    () => safeCohorts.filter(c => c.status === 'upcoming').length,
    [safeCohorts]
  );

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleAddCohort = async (e) => {
    e.preventDefault();

    const payload = {
      name: newCohort.name,
      start_date: newCohort.startDate,
      end_date: newCohort.endDate,
      max_capacity: newCohort.maxCapacity
    };

    const result = await addCohort(payload);

    if (result?.cohort && !result?.error) {
      setShowAddModal(false);
      setNewCohort({ name: '', startDate: '', endDate: '', maxCapacity: 30 });
    } else {
      alert(result?.error || 'Failed to create cohort. Check console for details.');
    }
  };

  const openDuplicateModal = (cohort) => {
    setSelectedCohortForDuplicate(cohort);
    setDuplicateForm({
      name: `${cohort.name} (Copy)`,
      startDate: '',
      endDate: ''
    });
    setShowDuplicateModal(true);
  };

  const handleDuplicateCohort = async (e) => {
    e.preventDefault();
    
    if (!selectedCohortForDuplicate || !duplicateForm.name || !duplicateForm.startDate || !duplicateForm.endDate) {
      alert('Please fill in all fields');
      return;
    }

    setIsDuplicating(true);
    
    try {
      const result = await duplicateCohort(selectedCohortForDuplicate.id, {
        name: duplicateForm.name,
        startDate: duplicateForm.startDate,
        endDate: duplicateForm.endDate
      });

      if (result?.cohort && !result?.error) {
        setShowDuplicateModal(false);
        setSelectedCohortForDuplicate(null);
        setDuplicateForm({ name: '', startDate: '', endDate: '' });
      } else {
        alert(result?.error || 'Failed to duplicate cohort. Check console for details.');
      }
    } catch (err) {
      alert('Error duplicating cohort: ' + (err.message || err));
    } finally {
      setIsDuplicating(false);
    }
  };

  const handleCohortClick = (cohortId) => {
    if (typeof setCurrentCohort === 'function') {
      setCurrentCohort(cohortId);
    }
    setCurrentView('cohort');
  };

  const handleExportBackup = async () => {
    setIsExporting(true);
    setExportProgress({ step: 0, total: 1, label: 'Starting...' });

    const result = await exportCourseBackup({
      includeStudentData,
      onProgress: (progress) => setExportProgress(progress)
    });

    setIsExporting(false);

    if (result.success) {
      setExportProgress(null);
      setShowExportModal(false);
      setIncludeStudentData(false);
    } else {
      alert('Export failed: ' + (result.error || 'Unknown error'));
      setExportProgress(null);
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="page-header">
        <div>
          <h1>Admin Dashboard</h1>
          <p>Manage cohorts, students, and course content</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-secondary" onClick={() => setShowExportModal(true)}>
            💾 Backup Data
          </button>
          <button className="btn-primary" onClick={() => setShowAddModal(true)}>
            + Add New Cohort
          </button>
        </div>
      </div>

      {/* Unread Messages Banner */}
      {unreadMessageCount > 0 && (
        <div className="unread-messages-banner" onClick={() => setCurrentView('messages')}>
          <div className="banner-content">
            <span className="banner-icon">📬</span>
            <span className="banner-text">
              You have <strong>{unreadMessageCount}</strong> unread message{unreadMessageCount !== 1 ? 's' : ''}
            </span>
          </div>
          <button className="banner-btn">View Messages →</button>
        </div>
      )}

      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon blue">📚</div>
          <div className="stat-content">
            <div className="stat-value">{safeCohorts.length}</div>
            <div className="stat-label">Total Cohorts</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon green">✅</div>
          <div className="stat-content">
            <div className="stat-value">{activeCohorts}</div>
            <div className="stat-label">Active Cohorts</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon purple">📅</div>
          <div className="stat-content">
            <div className="stat-value">{upcomingCohorts}</div>
            <div className="stat-label">Upcoming Cohorts</div>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon red">👥</div>
          <div className="stat-content">
            <div className="stat-value">{activeStudents}/{totalStudents}</div>
            <div className="stat-label">Active Students</div>
          </div>
        </div>
      </div>

      {/* Cohorts Table */}
      <div className="section-header">
        <h2>All Cohorts</h2>
      </div>

      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Cohort Name</th>
              <th>Status</th>
              <th>Dates</th>
              <th>Enrollment</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {safeCohorts.map(cohort => {
              const start = cohort.start_date || cohort.startDate;
              const end = cohort.end_date || cohort.endDate;

              const maxCap = cohort.max_capacity ?? cohort.maxCapacity ?? 0;

              // You don't have enrolledCount in schema; keep at 0 unless you add it or compute from profiles
              const enrolledCount = cohort.enrolledCount ?? 0;

              const pct = maxCap > 0 ? (enrolledCount / maxCap) * 100 : 0;

              return (
                <tr key={cohort.id}>
                  <td>
                    <div
                      className="cohort-name-cell clickable"
                      onClick={() => handleCohortClick(cohort.id)}
                    >
                      <span className="table-icon">📚</span>
                      {cohort.name}
                    </div>
                  </td>

                  <td>
                    <span className={`status-badge ${cohort.status}`}>
                      {(cohort.status || '').charAt(0).toUpperCase() + (cohort.status || '').slice(1)}
                    </span>
                  </td>

                  <td className="date-cell">
                    {formatDate(start)} - {formatDate(end)}
                  </td>

                  <td>
                    <div className="enrollment-cell">
                      <div className="enrollment-bar">
                        <div
                          className="enrollment-fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="enrollment-text">
                        {enrolledCount}/{maxCap}
                      </span>
                    </div>
                  </td>

                  <td>
                    <div className="action-buttons">
                      <button
                        className="btn-icon"
                        title="View Cohort"
                        onClick={() => handleCohortClick(cohort.id)}
                      >
                        👁️
                      </button>

                      <button
                        className="btn-icon"
                        title="Duplicate Cohort"
                        onClick={() => openDuplicateModal(cohort)}
                      >
                        📋
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {safeCohorts.length === 0 && (
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '24px' }}>
                  No cohorts found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Cohort Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Cohort</h3>
              <button className="modal-close" onClick={() => setShowAddModal(false)}>x</button>
            </div>

            <form onSubmit={handleAddCohort}>
              <div className="form-group">
                <label>Cohort Name</label>
                <input
                  type="text"
                  placeholder="e.g., April 2025 Cohort"
                  value={newCohort.name}
                  onChange={(e) => setNewCohort({ ...newCohort, name: e.target.value })}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={newCohort.startDate}
                    onChange={(e) => setNewCohort({ ...newCohort, startDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={newCohort.endDate}
                    onChange={(e) => setNewCohort({ ...newCohort, endDate: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Max Capacity</label>
                <input
                  type="number"
                  min="1"
                  value={newCohort.maxCapacity}
                  onChange={(e) => setNewCohort({ ...newCohort, maxCapacity: parseInt(e.target.value, 10) })}
                  required
                />
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Create Cohort
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Duplicate Cohort Modal */}
      {showDuplicateModal && selectedCohortForDuplicate && (
        <div className="modal-overlay" onClick={() => !isDuplicating && setShowDuplicateModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Duplicate Cohort</h3>
              <button className="modal-close" onClick={() => !isDuplicating && setShowDuplicateModal(false)}>x</button>
            </div>

            <p className="modal-description">
              Create a copy of <strong>{selectedCohortForDuplicate.name}</strong> with new dates.
            </p>

            <form onSubmit={handleDuplicateCohort}>
              <div className="form-group">
                <label>New Cohort Name</label>
                <input
                  type="text"
                  value={duplicateForm.name}
                  onChange={(e) => setDuplicateForm({ ...duplicateForm, name: e.target.value })}
                  disabled={isDuplicating}
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date</label>
                  <input
                    type="date"
                    value={duplicateForm.startDate}
                    onChange={(e) => setDuplicateForm({ ...duplicateForm, startDate: e.target.value })}
                    disabled={isDuplicating}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date</label>
                  <input
                    type="date"
                    value={duplicateForm.endDate}
                    onChange={(e) => setDuplicateForm({ ...duplicateForm, endDate: e.target.value })}
                    disabled={isDuplicating}
                    required
                  />
                </div>
              </div>

              <div className="info-box">
                <span className="info-icon">i</span>
                <p>This will copy syllabus PDFs, custom events (with adjusted dates), and module visibility settings.</p>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={() => setShowDuplicateModal(false)} disabled={isDuplicating}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isDuplicating}>
                  {isDuplicating ? 'Duplicating...' : 'Duplicate Cohort'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Export Backup Modal */}
      {showExportModal && (
        <div className="modal-overlay" onClick={() => !isExporting && setShowExportModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '480px' }}>
            <div className="modal-header">
              <h3>Backup Course Data</h3>
              <button className="modal-close" onClick={() => !isExporting && setShowExportModal(false)}>x</button>
            </div>

            <div style={{ padding: '0 24px 16px' }}>
              <p style={{ color: '#6b7280', marginBottom: '16px', lineHeight: '1.5' }}>
                Download a JSON backup of all course structure data including cohorts, modules, 
                weekly content, calendar events, syllabus files, and settings.
              </p>

              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px', 
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                cursor: isExporting ? 'not-allowed' : 'pointer',
                marginBottom: '12px'
              }}>
                <input
                  type="checkbox"
                  checked={includeStudentData}
                  onChange={(e) => setIncludeStudentData(e.target.checked)}
                  disabled={isExporting}
                  style={{ width: '18px', height: '18px' }}
                />
                <div>
                  <div style={{ fontWeight: '500' }}>Include student data</div>
                  <div style={{ fontSize: '13px', color: '#9ca3af' }}>
                    Profiles, progress, submissions, notes, percentiles, attendance
                  </div>
                </div>
              </label>

              {isExporting && exportProgress && (
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    fontSize: '13px', 
                    color: '#6b7280',
                    marginBottom: '6px' 
                  }}>
                    <span>Exporting: {exportProgress.label}</span>
                    <span>{exportProgress.step}/{exportProgress.total}</span>
                  </div>
                  <div style={{ 
                    height: '6px', 
                    backgroundColor: '#e5e7eb', 
                    borderRadius: '3px', 
                    overflow: 'hidden' 
                  }}>
                    <div style={{ 
                      height: '100%', 
                      width: `${(exportProgress.step / exportProgress.total) * 100}%`,
                      backgroundColor: '#dc2626',
                      borderRadius: '3px',
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={() => { setShowExportModal(false); setIncludeStudentData(false); }}
                disabled={isExporting}
              >
                Cancel
              </button>
              <button 
                className="btn-primary" 
                onClick={handleExportBackup}
                disabled={isExporting}
              >
                {isExporting ? 'Exporting...' : '💾 Download Backup'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
